import { createServerFn } from "@tanstack/react-start";

export type FilmStatus = "draft" | "published" | "hidden";

export type PublicFilm = {
  id: string;
  title: string;
  play_date: string; // ISO date (YYYY-MM-DD)
  festival_day: number;
  teaser: string | null;
  description: string | null;
  poster_url: string | null; // resolved (public or signed URL)
  start_time: string | null;
  duration_minutes: number | null;
  genre: string | null;
  age_rating: string | null;
  badges: string[];
  cta_label: string | null;
  cta_url: string | null;
  sort_order: number;
  trailer_url: string | null;
  show_activities_badge: boolean;
  activities_badge_label: string | null;
};

export type PublicProgramPayload = {
  sectionVisible: boolean;
  films: PublicFilm[];
};

/**
 * Public program feed. Returns homepage-flagged published films only when the
 * global `homepage_program_section_visible` toggle is on.
 */
export const getPublicProgram = createServerFn({ method: "GET" }).handler(
  async (): Promise<PublicProgramPayload> => {
    const { cached } = await import("@/lib/server-cache.server");
    return cached("program-home", 120_000, async () => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");

    const { data: setting } = await supabaseAdmin
      .from("site_settings")
      .select("value")
      .eq("key", "homepage_program_section_visible")
      .maybeSingle();
    const sectionVisible = setting?.value === true || setting?.value === "true";

    if (!sectionVisible) return { sectionVisible: false, films: [] };

    const { data, error } = await (supabaseAdmin as any)
      .from("festival_films")
      .select(
        "id,title,play_date,festival_day,teaser,description,poster_url,start_time,duration_minutes,genre,age_rating,badges,cta_label,cta_url,sort_order,status,show_on_homepage,trailer_url,show_activities_badge,activities_badge_label",
      )
      .eq("status", "published")
      .eq("show_on_homepage", true)
      .order("play_date", { ascending: true })
      .order("sort_order", { ascending: true });

    if (error || !data) return { sectionVisible: true, films: [] };

    const films: PublicFilm[] = [];
    for (const row of data as Array<Record<string, unknown>>) {
      const rawPoster = (row.poster_url as string | null) ?? null;
      let poster: string | null = null;
      if (rawPoster) {
        if (/^https?:\/\//i.test(rawPoster)) {
          poster = rawPoster;
        } else {
          const { data: signed } = await supabaseAdmin.storage
            .from("film-posters")
            .createSignedUrl(rawPoster, 60 * 60 * 24);
          poster = signed?.signedUrl ?? null;
        }
      }
      films.push({
        id: row.id as string,
        title: row.title as string,
        play_date: row.play_date as string,
        festival_day: (row.festival_day as number) ?? 0,
        teaser: (row.teaser as string | null) ?? null,
        description: (row.description as string | null) ?? null,
        poster_url: poster,
        start_time: (row.start_time as string | null) ?? null,
        duration_minutes: (row.duration_minutes as number | null) ?? null,
        genre: (row.genre as string | null) ?? null,
        age_rating: (row.age_rating as string | null) ?? null,
        badges: ((row.badges as string[] | null) ?? []) as string[],
        cta_label: (row.cta_label as string | null) ?? null,
        cta_url: (row.cta_url as string | null) ?? null,
        sort_order: (row.sort_order as number) ?? 0,
        trailer_url: (row.trailer_url as string | null) ?? null,
        show_activities_badge: (row.show_activities_badge as boolean) ?? false,
        activities_badge_label: (row.activities_badge_label as string | null) ?? null,
      });
    }
    return { sectionVisible: true, films };
    });
  },
);

/**
 * Full public program feed for the dedicated /programma page.
 * Returns all published films regardless of the homepage toggle.
 */
export const getFullProgram = createServerFn({ method: "GET" }).handler(
  async (): Promise<PublicFilm[]> => {
    const { cached } = await import("@/lib/server-cache.server");
    return cached("program-full", 120_000, async () => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");

    const { data, error } = await (supabaseAdmin as any)
      .from("festival_films")
      .select(
        "id,title,play_date,festival_day,teaser,description,poster_url,start_time,duration_minutes,genre,age_rating,badges,cta_label,cta_url,sort_order,status,trailer_url,show_activities_badge,activities_badge_label",
      )
      .eq("status", "published")
      .order("play_date", { ascending: true })
      .order("sort_order", { ascending: true });

    if (error || !data) return [];

    const films: PublicFilm[] = [];
    for (const row of data as Array<Record<string, unknown>>) {
      const rawPoster = (row.poster_url as string | null) ?? null;
      let poster: string | null = null;
      if (rawPoster) {
        if (/^https?:\/\//i.test(rawPoster)) {
          poster = rawPoster;
        } else {
          const { data: signed } = await supabaseAdmin.storage
            .from("film-posters")
            .createSignedUrl(rawPoster, 60 * 60 * 24);
          poster = signed?.signedUrl ?? null;
        }
      }
      films.push({
        id: row.id as string,
        title: row.title as string,
        play_date: row.play_date as string,
        festival_day: (row.festival_day as number) ?? 0,
        teaser: (row.teaser as string | null) ?? null,
        description: (row.description as string | null) ?? null,
        poster_url: poster,
        start_time: (row.start_time as string | null) ?? null,
        duration_minutes: (row.duration_minutes as number | null) ?? null,
        genre: (row.genre as string | null) ?? null,
        age_rating: (row.age_rating as string | null) ?? null,
        badges: ((row.badges as string[] | null) ?? []) as string[],
        cta_label: (row.cta_label as string | null) ?? null,
        cta_url: (row.cta_url as string | null) ?? null,
        sort_order: (row.sort_order as number) ?? 0,
        trailer_url: (row.trailer_url as string | null) ?? null,
        show_activities_badge: (row.show_activities_badge as boolean) ?? false,
        activities_badge_label: (row.activities_badge_label as string | null) ?? null,
      });
    }
    return films;
    });
  },
);